import * as XLSX from "xlsx-js-style";
import { ItemPlanilha, TipoItem } from "@models/index";
import { baixarArquivo } from "../utils/nome-arquivo";
import {
  taxaDeHonorarios,
  PERCENT_IMPOSTOS,
  PERCENT_TAXA_ADMINISTRACAO,
} from "../utils/planilhaTotais";

// Interface para os totais da planilha
export interface TotaisPlanilha {
  subtotal: number;
  totalServicosEssenciais: number;
  totalServicosComplementares: number;
  /** Parcela de complementares que está no subgrupo TAXAS E SEGUROS. */
  totalTaxasESeguros: number;
  impostos: number;
  totalHonorarios: number;
  taxaAdministracao: number;
  totalValorAS: number;
  totalDespesasAcessorias: number;
  totalGeralEvento: number;
  // Campos editáveis do breakdown de Serviços Complementares
  breakdownEquipamentos?: number;
  breakdownMateriais?: number;
  breakdownServicos?: number;
}

export interface OpcoesExportacao {
  /**
   * Inclui fornecedores que ainda não responderam, e os itens que só têm
   * esses. O padrão é deixá-los de fora: é a planilha que vai ao cliente e ao
   * FACITE, e cotação sem resposta não tem valor a mostrar.
   */
  incluirSemResposta?: boolean;
}

/** O que a coluna de observações diz de um fornecedor que não respondeu. */
const situacaoSemResposta = (fornecedor: ItemPlanilha, status: string) => {
  if (status === "rejeitada") return fornecedor.observacoes || "Recusou cotar";
  if (status === "enviada" || status === "reenviada")
    return "Aguardando resposta";
  return "Cotação não solicitada";
};

export const excelExportService = {
  exportToExcel: (
    planilha: any,
    rowData: ItemPlanilha[],
    totais: TotaisPlanilha,
    opcoes: OpcoesExportacao = {},
  ) => {
    const buffer = excelExportService.generateExcelBuffer(
      planilha,
      rowData,
      totais,
      opcoes,
    );

    // Fazer download do arquivo
    const nomeEvento = planilha?.evento || "Evento";
    const dataAtual = new Date()
      .toLocaleDateString("pt-BR")
      .replace(/\//g, "-");
    // Criar blob e fazer download
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    baixarArquivo(blob, `Planilha_${nomeEvento}_${dataAtual}`, "xlsx");
  },

  // Novo método que retorna apenas o buffer sem fazer download
  generateExcelBuffer: (
    planilha: any,
    rowData: ItemPlanilha[],
    totais: TotaisPlanilha,
    { incluirSemResposta = false }: OpcoesExportacao = {},
  ): ArrayBuffer => {
    const statusDe = (f: ItemPlanilha) =>
      String(f.status || "")
        .toLowerCase()
        .trim();

    // A contagem que decide se o item entra e quantas linhas a coluna B
    // mescla precisa usar a mesma regra que escreve as linhas de fornecedor.
    // Regras diferentes desalinhavam a mesclagem com as linhas escritas.
    // Linha sem fornecedor escolhido não entra nem no modo "todos".
    const fornecedorEntra = (f: ItemPlanilha) =>
      statusDe(f) === "respondida" ||
      (incluirSemResposta && !!f.item?.trim());

    const wb = XLSX.utils.book_new();
    const ws: any = {};

    // Função helper para criar célula com estilo
    const cell = (value: any, style: any = {}, isCurrency: boolean = false) => {
      const defaultStyle = {
        font: { name: "Trebuchet MS", sz: 14 },
        alignment: { vertical: "center", horizontal: "left", wrapText: true },
      };

      // Formato de moeda brasileiro
      const currencyFormat = "R$ #,##0.00";

      const cellObj: any = {
        v: value === null || value === undefined ? "" : value,
        t: typeof value === "number" ? "n" : "s",
        s: {
          ...defaultStyle,
          ...style,
        },
      };

      // Aplicar formato de moeda se solicitado
      if (isCurrency && typeof value === "number") {
        cellObj.z = currencyFormat;
      }

      return cellObj;
    };

    /**
     * Célula de fórmula. `v` guarda o valor calculado aqui como resultado em
     * cache: leitor que não recalcula (pré-visualização, Google Sheets em modo
     * leitura, conversores) ainda mostra o número certo, e o Excel recalcula
     * ao abrir. Separador decimal na fórmula é sempre ponto — o formato do
     * arquivo não depende do idioma de quem abre.
     */
    const cellFormula = (
      formula: string,
      valorCalculado: number,
      style: any = {},
      isCurrency: boolean = true,
    ) => {
      const cellObj: any = {
        f: formula,
        v: valorCalculado,
        t: "n",
        s: {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { vertical: "center", horizontal: "left", wrapText: true },
          ...style,
        },
      };
      if (isCurrency) cellObj.z = "R$ #,##0.00";
      return cellObj;
    };

    if (!ws["!merges"]) ws["!merges"] = [];

    let row = 1;

    // LINHAS 1-2: Header - PLANEJAMENTO DE CUSTOS DO EVENTO
    ws["B1"] = cell("PLANEJAMENTO DE CUSTOS DO EVENTO", {
      font: {
        name: "Trebuchet MS",
        sz: 20,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
    });
    // Criar células vazias para a mesclagem B1:J2
    for (let c = 2; c <= 9; c++) {
      ws[XLSX.utils.encode_cell({ r: 0, c })] = cell("", {
        fill: { fgColor: { rgb: "000000" } },
      });
    }
    for (let c = 1; c <= 9; c++) {
      ws[XLSX.utils.encode_cell({ r: 1, c })] = cell("", {
        fill: { fgColor: { rgb: "000000" } },
      });
    }
    ws["!merges"].push({ s: { r: 0, c: 1 }, e: { r: 1, c: 9 } });

    // LINHA 3: Vazia
    row = 3;

    // LINHA 4: Cliente e Data
    row = 4;
    ws[`B${row}`] = cell(`CLIENTE: ${planilha?.clienteNome || ""}`, {
      font: { name: "Trebuchet MS", sz: 14, bold: true },
    });
    // Criar células vazias para mesclagem B4:D4
    ws[`C${row}`] = cell("");
    ws[`D${row}`] = cell("");
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 3 } });

    // Mesclar E4:J4 para data/período/local
    const formatarPeriodo = (inicio?: string | Date, fim?: string | Date) => {
      if (!inicio && !fim) return "";

      // Parse seguro para evitar offset de fuso horário
      const parse = (d: any): Date | undefined => {
        if (!d) return undefined;
        if (d instanceof Date) return d;
        if (typeof d === "string") {
          // Se vier no formato ISO curto (YYYY-MM-DD), montar Date local
          const isoMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (isoMatch) {
            const year = Number(isoMatch[1]);
            const month = Number(isoMatch[2]) - 1; // 0-based
            const day = Number(isoMatch[3]);
            return new Date(year, month, day);
          }
        }
        // Fallback: deixar o JS interpretar
        return new Date(d);
      };

      const di = parse(inicio);
      const df = parse(fim);
      if (!di && !df) return "";

      const optsMes = { month: "short" } as const;
      const dia = (d: Date) => d.getDate().toString().padStart(2, "0");
      const mesExt = (d: Date) =>
        d.toLocaleDateString("pt-BR", optsMes).replace(".", ""); // ex: "out" em vez de "out."

      if (di && df) {
        if (
          di.getMonth() === df.getMonth() &&
          di.getFullYear() === df.getFullYear()
        ) {
          // Mesmo mês: "15 a 18 outubro"
          return `${dia(di)} a ${dia(df)} ${mesExt(df)}`;
        }
        // Meses diferentes: "28 set a 02 out"
        return `${dia(di)} ${mesExt(di)} a ${dia(df)} ${mesExt(df)}`;
      }

      // Só uma das datas
      const d = di || df!;
      return `${dia(d)} ${mesExt(d)}`;
    };

    const periodoEvento = formatarPeriodo(
      planilha?.dataInicio,
      planilha?.dataFim,
    );
    const localEvento = planilha?.localEvento
      ? ` - ${planilha.localEvento}`
      : "";
    const textoDataEvento = periodoEvento
      ? `DATA DO EVENTO/AÇÃO: ${periodoEvento}${localEvento}`
      : "DATA DO EVENTO/AÇÃO:";

    ws[`E${row}`] = cell(textoDataEvento, {
      font: { name: "Trebuchet MS", sz: 14, bold: true },
    });
    // Criar células vazias para mesclagem E4:J4
    for (let c = 5; c <= 9; c++) {
      ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("");
    }
    ws["!merges"].push({ s: { r: row - 1, c: 4 }, e: { r: row - 1, c: 9 } });

    // LINHA 5: Evento/Ação
    row = 5;
    ws[`B${row}`] = cell(`EVENTO/AÇÃO: ${planilha?.evento || ""}`, {
      font: { name: "Trebuchet MS", sz: 14, bold: true },
    });
    // Criar células vazias para mesclagem B5:D5
    ws[`C${row}`] = cell("");
    ws[`D${row}`] = cell("");
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 3 } });

    // LINHA 6: Vazia
    row = 6;

    // PROCESSAR DADOS DINÂMICOS A PARTIR DA LINHA 7
    row = 7;

    let currentGrupoNumero = 1;
    let currentItemNumero = 1;
    let acessoriaDescCounter = 0; // contador de linhas DESCRITIVO_ACESSORIA dentro do item atual
    const subgruposInfo: Array<{
      startRow: number;
      endRow?: number;
      total: number;
      grupoNumero: number;
      titulo?: string;
    }> = [];
    let currentSubgrupoInfo: {
      startRow: number;
      endRow?: number;
      total: number;
      grupoNumero: number;
      titulo?: string;
    } | null = null;
    const subgrupoRows: number[] = []; // Rastrear linhas de subgrupo para definir altura

    for (let i = 0; i < rowData.length; i++) {
      const dataRow = rowData[i];

      if (dataRow.tipo === TipoItem.GRUPO) {
        // Antes de abrir um novo GRUPO, fechar o subgrupo atual (se existir)
        // usando a regra: fim = linha do cabeçalho atual - 1.
        if (currentSubgrupoInfo) {
          currentSubgrupoInfo.endRow = row - 1;
          subgruposInfo.push(currentSubgrupoInfo);
          currentSubgrupoInfo = null;
        }

        // PASSO 11: LINHA DO TIPO GRUPO
        // Extrair número do grupo (ex: "ITEM 2 - ..." => 2)
        const grupoMatch = dataRow.item.match(/ITEM\s+(\d+)/i);
        if (grupoMatch) {
          currentGrupoNumero = parseInt(grupoMatch[1]);
          currentItemNumero = 1; // Resetar contador de itens
        }

        ws[`B${row}`] = cell(dataRow.item, {
          font: {
            name: "Trebuchet MS",
            sz: 20,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "808080" } },
          alignment: { horizontal: "left", vertical: "center" },
        });
        // Criar células vazias para mesclar B até J
        for (let c = 2; c <= 9; c++) {
          ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("", {
            fill: { fgColor: { rgb: "808080" } },
          });
        }
        ws["!merges"].push({
          s: { r: row - 1, c: 1 },
          e: { r: row - 1, c: 9 },
        });
        row++;
      } else if (dataRow.tipo === TipoItem.SUBGRUPO) {
        // Finalizar subgrupo anterior se existir usando a regra:
        // início = linha do cabeçalho anterior + 1, fim = linha do
        // cabeçalho atual - 1.
        if (currentSubgrupoInfo) {
          currentSubgrupoInfo.endRow = row - 1;
          subgruposInfo.push(currentSubgrupoInfo);
        }

        // PASSO 12: LINHA DO TIPO SUBGRUPO
        // B e C mescladas com o conteúdo do item
        ws[`B${row}`] = cell(dataRow.item, {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });
        ws[`C${row}`] = cell("", {
          fill: { fgColor: { rgb: "000000" } },
        });
        ws["!merges"].push({
          s: { r: row - 1, c: 1 },
          e: { r: row - 1, c: 2 },
        });

        ws[`D${row}`] = cell("DESCRITIVO", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        ws[`E${row}`] = cell("Qtde", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        ws[`F${row}`] = cell("Diária\n(Quando aplicável)", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
        });

        ws[`G${row}`] = cell("Unitário (R$)", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        ws[`H${row}`] = cell("Sub-Totais (R$)", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        ws[`I${row}`] = cell("TOTAL (R$)", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        ws[`J${row}`] = cell("OBSERVAÇÕES", {
          font: {
            name: "Trebuchet MS",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
        });

        // Registrar linha de subgrupo para definir altura depois
        subgrupoRows.push(row);
        row++;
        // NÃO resetar currentItemNumero aqui - continua a numeração dentro do grupo

        // Início do subgrupo é sempre a linha imediatamente após
        // o cabeçalho preto (regra: subgrupo+1).
        currentSubgrupoInfo = {
          startRow: row,
          total: 0,
          grupoNumero: currentGrupoNumero,
          titulo: dataRow.item,
        };
      } else if (
        dataRow.tipo === TipoItem.ITEM ||
        dataRow.tipo === TipoItem.ITEM_TAXAS ||
        dataRow.tipo === TipoItem.ITEM_TRANSPORTE ||
        dataRow.tipo === "item_acessoria" ||
        dataRow.tipo === "descritivo_acessoria"
      ) {
        // PASSO 13: LINHA DO TIPO ITEM
        const itemNumero = `${currentGrupoNumero}.${currentItemNumero}`;

        // Definir se aplica cor de fundo (apenas para grupo 2+)
        const isGrupo1 = currentGrupoNumero === 1;

        const isAcessoriaItem = dataRow.tipo === "item_acessoria";
        const isAcessoriaDesc = dataRow.tipo === "descritivo_acessoria";

        // Rastrear contador de linhas brancas dentro de cada ITEM_ACESSORIA
        if (isAcessoriaItem) {
          acessoriaDescCounter = 0;
        } else if (isAcessoriaDesc) {
          acessoriaDescCounter++;
        }

        // Acumular total no subgrupo (APENAS para itens que têm subtotal - não para descritivo_acessoria)
        if (currentSubgrupoInfo && !isAcessoriaDesc && dataRow.subtotal) {
          currentSubgrupoInfo.total += dataRow.subtotal;
        }

        // Contar quantos fornecedores RESPONDIDOS este item tem
        let fornecedorCount = 0;
        for (
          let j = i + 1;
          j < rowData.length &&
          (rowData[j].tipo === TipoItem.FORNECEDOR ||
            rowData[j].tipo === "fornecedor");
          j++
        ) {
          if (fornecedorEntra(rowData[j])) {
            fornecedorCount++;
          }
        }

        // Se não houver fornecedores respondidos E o item tiver fornecedores (mas não respondidos),
        // pular este item completamente (não adicionar à planilha)
        const temFornecedores = rowData
          .slice(i + 1)
          .some(
            (r) =>
              (r.tipo === TipoItem.FORNECEDOR ||
                (r as any).tipo === "fornecedor") &&
              r.numero?.startsWith(dataRow.numero + "."),
          );

        if (temFornecedores && fornecedorCount === 0 && !isAcessoriaItem) {
          // Pular este item e seus fornecedores
          while (
            i + 1 < rowData.length &&
            (rowData[i + 1].tipo === TipoItem.FORNECEDOR ||
              rowData[i + 1].tipo === "fornecedor")
          ) {
            i++; // Pular fornecedores
          }
          continue; // Não processar este item
        }

        let acessoriaDescCount = 0;
        if (isAcessoriaItem) {
          for (let lookAhead = i + 1; lookAhead < rowData.length; lookAhead++) {
            const nextRow: any = rowData[lookAhead];
            if (nextRow.tipo === "descritivo_acessoria") acessoriaDescCount++;
            else break;
          }
        }

        // Linhas brancas que acompanham o item amarelo. TAXAS tem sempre 3;
        // TRANSPORTE tem quantas o usuário criou, então é preciso contar.
        let descritivoTaxasCount = 0;
        if (dataRow.tipo === TipoItem.ITEM_TAXAS) {
          descritivoTaxasCount = 3; // Sempre 3 linhas para TAXAS E SEGUROS
        } else if (dataRow.tipo === TipoItem.ITEM_TRANSPORTE) {
          for (let k = i + 1; k < rowData.length; k++) {
            if (rowData[k].tipo !== TipoItem.DESCRITIVO_TRANSPORTE) break;
            descritivoTaxasCount++;
          }
        }

        // Coluna B - numeração (mescla especial para acessórios e TAXAS E SEGUROS)
        if (!isAcessoriaDesc) {
          ws[`B${row}`] = cell(itemNumero, {
            font: { name: "Trebuchet MS", sz: 14, bold: true },
            alignment: { horizontal: "center", vertical: "center" },
          });
          const mergeSpanB = Math.max(
            fornecedorCount,
            acessoriaDescCount,
            descritivoTaxasCount,
          );

          if (mergeSpanB > 0) {
            ws["!merges"].push({
              s: { r: row - 1, c: 1 },
              e: { r: row - 1 + mergeSpanB, c: 1 },
            });
          }
        }

        // Coluna C - Nome do item
        const styleC: any = {
          font: { name: "Trebuchet MS", sz: 14, bold: true },
          alignment: { horizontal: "left", vertical: "center", wrapText: true },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleC.fill = { fgColor: { rgb: "FFFF00" } };
        // Mesma regra da linha branca de taxas: na acessória a coluna C é o
        // rótulo da posição e o nome do fornecedor vai para a D. Antes o campo
        // guardava o texto "Fornecedor N" e as duas coisas coincidiam por
        // acaso; desde que ele virou seleção de fornecedor, o nome passou a
        // subir para a C.
        ws[`C${row}`] = cell(
          isAcessoriaDesc
            ? `Fornecedor ${acessoriaDescCounter}`
            : dataRow.item || "",
          styleC,
        );

        // Coluna D - Descritivo
        const styleD: any = {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { horizontal: "left", vertical: "center", wrapText: true },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleD.fill = { fgColor: { rgb: "FFFF00" } };
        ws[`D${row}`] = cell(
          isAcessoriaDesc ? dataRow.item || "" : dataRow.descritivo || "",
          styleD,
        );

        // Coluna E - Quantidade
        const styleE: any = {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleE.fill = { fgColor: { rgb: "FFFF00" } };
        ws[`E${row}`] = cell(dataRow.quantidade || "", styleE);

        // Coluna F - Diária
        const styleF: any = {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleF.fill = { fgColor: { rgb: "FFFF00" } };
        ws[`F${row}`] = cell(dataRow.diaria || "", styleF);

        // Coluna G - Unitário
        const styleG: any = {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { horizontal: "right", vertical: "center" },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleG.fill = { fgColor: { rgb: "FFFF00" } };
        ws[`G${row}`] = cell(dataRow.unitario || "", styleG, true);

        // Coluna H - Subtotal (mesclar verticalmente se houver fornecedores OU descritivos acessórios, BG verde somente para grupo 2+)
        const styleH: any = {
          font: { name: "Trebuchet MS", sz: 14, bold: true },
          alignment: { horizontal: "right", vertical: "center" },
        };
        if (!isGrupo1 && !isAcessoriaDesc)
          styleH.fill = { fgColor: { rgb: "92D050" } };
        const subtotalValue = isAcessoriaDesc ? "" : dataRow.subtotal || "";

        // Base da cadeia de fórmulas: com quantidade, diária e unitário
        // presentes, o subtotal vira E*F*G e passa a recalcular quando
        // qualquer um dos três é editado no Excel.
        //
        // Só quando o produto CONFERE com o subtotal gravado. Item de TAXAS e
        // de despesa acessória tem valor lançado à mão, sem relação com
        // quantidade × diária — ali a fórmula devolveria outro número, e o
        // valor é mantido.
        const produtoConfere =
          !isAcessoriaDesc &&
          !!dataRow.quantidade &&
          !!dataRow.diaria &&
          dataRow.unitario != null &&
          Math.abs(
            dataRow.quantidade * dataRow.diaria * dataRow.unitario -
              (dataRow.subtotal || 0),
          ) < 0.01;

        ws[`H${row}`] = produtoConfere
          ? cellFormula(
              `E${row}*F${row}*G${row}`,
              dataRow.subtotal as number,
              styleH,
            )
          : cell(subtotalValue, styleH, !isAcessoriaDesc && !!dataRow.subtotal);

        // Mesclar H verticalmente se houver fornecedores ou descritivos acessórios ou descritivos taxas
        const mergeSpanH = Math.max(
          fornecedorCount,
          acessoriaDescCount,
          descritivoTaxasCount,
        );
        if (mergeSpanH > 0) {
          ws["!merges"].push({
            s: { r: row - 1, c: 7 },
            e: { r: row - 1 + mergeSpanH, c: 7 },
          });
          // Mesclar J (Observações) verticalmente também
          ws["!merges"].push({
            s: { r: row - 1, c: 9 },
            e: { r: row - 1 + mergeSpanH, c: 9 },
          });
        }

        // Coluna I - Total do subgrupo (será mesclado verticalmente depois para todo o subgrupo)

        // Coluna J - Observações
        ws[`J${row}`] = cell(dataRow.observacoes || "", {
          font: { name: "Trebuchet MS", sz: 14 },
          alignment: { horizontal: "left", vertical: "center" },
        });

        row++;
        if (dataRow.tipo !== "descritivo_acessoria") {
          currentItemNumero++;
        }

        // PROCESSAR as linhas brancas de TAXAS e de TRANSPORTES
        if (
          dataRow.tipo === TipoItem.ITEM_TAXAS ||
          dataRow.tipo === TipoItem.ITEM_TRANSPORTE
        ) {
          const tipoLinhaBranca =
            dataRow.tipo === TipoItem.ITEM_TRANSPORTE
              ? TipoItem.DESCRITIVO_TRANSPORTE
              : TipoItem.DESCRITIVO_TAXAS;

          const descritivosTaxas: ItemPlanilha[] = [];
          let tempIndex = i + 1;
          while (
            tempIndex < rowData.length &&
            rowData[tempIndex].tipo === tipoLinhaBranca
          ) {
            descritivosTaxas.push(rowData[tempIndex]);
            tempIndex++;
          }

          // Avançar o índice principal
          i += descritivosTaxas.length;

          // Sempre garantir 3 linhas (preencher com vazias se necessário)
          const linhasParaMostrar = 3;
          for (let j = 0; j < linhasParaMostrar; j++) {
            const descritivo = descritivosTaxas[j]; // Pode ser undefined se não houver dados

            // Coluna B já foi mesclada acima

            // Coluna C - o rótulo da posição, nunca o nome.
            //
            // Estava saindo o nome do fornecedor aqui, diferente do que o item
            // comum faz mais abaixo neste mesmo arquivo. A planilha ficava com
            // duas convenções: em equipamentos a coluna C dizia "Fornecedor 1"
            // e o nome ia na D; em taxas e transportes o nome subia para a C e
            // a D vinha vazia.
            ws[`C${row}`] = cell(`Fornecedor ${j + 1}`, {
              font: { name: "Trebuchet MS", sz: 14 },
              alignment: { horizontal: "left", vertical: "center" },
            });

            // Coluna D - o nome do fornecedor, como no item comum. O descritivo
            // da linha branca não entra: quem descreve o serviço é a linha
            // amarela acima, e repetir aqui duplicava a informação.
            ws[`D${row}`] = cell(descritivo?.item || "", {
              font: { name: "Trebuchet MS", sz: 14 },
              alignment: {
                horizontal: "left",
                vertical: "center",
                wrapText: true,
              },
            });

            // Coluna E - Quantidade (repetir do item)
            ws[`E${row}`] = cell(dataRow.quantidade || "", {
              font: { name: "Trebuchet MS", sz: 14 },
              alignment: { horizontal: "center", vertical: "center" },
            });

            // Coluna F - Diária (repetir do item)
            ws[`F${row}`] = cell(dataRow.diaria || "", {
              font: { name: "Trebuchet MS", sz: 14 },
              alignment: { horizontal: "center", vertical: "center" },
            });

            // Coluna G - Unitário do descritivo
            ws[`G${row}`] = cell(
              descritivo?.unitario || "",
              {
                font: { name: "Trebuchet MS", sz: 14 },
                alignment: { horizontal: "right", vertical: "center" },
              },
              descritivo?.unitario != null,
            );

            // Coluna H já foi mesclada acima

            // Coluna J - Observações
            ws[`J${row}`] = cell(descritivo?.observacoes || "", {
              font: { name: "Trebuchet MS", sz: 14 },
              alignment: { horizontal: "left", vertical: "center" },
            });

            row++;
          }
        }

        // PASSO 14 e 15: PROCESSAR APENAS FORNECEDORES QUE RESPONDERAM
        let fornecedorNumero = 1;
        while (
          i + 1 < rowData.length &&
          (rowData[i + 1].tipo === TipoItem.FORNECEDOR ||
            rowData[i + 1].tipo === "fornecedor")
        ) {
          i++;
          const fornecedor = rowData[i];

          if (!fornecedorEntra(fornecedor)) {
            continue;
          }
          // Sem resposta, o preço fica vazio: valor digitado sem finalizar não
          // tem PDF e não vale como cotação.
          const respondeu = statusDe(fornecedor) === "respondida";

          // Coluna B já foi mesclada acima

          // Coluna C - "Fornecedor N"
          ws[`C${row}`] = cell(`Fornecedor ${fornecedorNumero}`, {
            font: { name: "Trebuchet MS", sz: 14 },
            alignment: { horizontal: "left", vertical: "center" },
          });

          // Coluna D - Nome do fornecedor
          ws[`D${row}`] = cell(fornecedor.item || "", {
            font: { name: "Trebuchet MS", sz: 14 },
            alignment: { horizontal: "left", vertical: "center" },
          });

          // Coluna E - Quantidade (repetir do item)
          ws[`E${row}`] = cell(dataRow.quantidade || "", {
            font: { name: "Trebuchet MS", sz: 14 },
            alignment: { horizontal: "center", vertical: "center" },
          });

          // Coluna F - Diária (repetir do item)
          ws[`F${row}`] = cell(dataRow.diaria || "", {
            font: { name: "Trebuchet MS", sz: 14 },
            alignment: { horizontal: "center", vertical: "center" },
          });

          // Coluna G - Unitário do fornecedor. Sem resposta, a situação dele
          // vai no lugar do preço: a coluna J está mesclada com a linha
          // amarela, e um texto escrito ali não apareceria.
          ws[`G${row}`] = respondeu
            ? cell(
                fornecedor.unitario || "",
                {
                  font: { name: "Trebuchet MS", sz: 14 },
                  alignment: { horizontal: "right", vertical: "center" },
                },
                true,
              )
            : cell(situacaoSemResposta(fornecedor, statusDe(fornecedor)), {
                font: { name: "Trebuchet MS", sz: 12, italic: true },
                alignment: { horizontal: "right", vertical: "center" },
              });

          // Coluna H já foi mesclada acima

          // Coluna J - Observações
          ws[`J${row}`] = cell(fornecedor.observacoes || "", {
            font: { name: "Trebuchet MS", sz: 14 },
            alignment: { horizontal: "left", vertical: "center" },
          });

          row++;
          fornecedorNumero++;
        }
      }
    }

    // Finalizar último subgrupo se existir
    if (currentSubgrupoInfo) {
      // Regra para o último subgrupo: ele vai da linha
      // do seu cabeçalho + 1 até a última linha de item/
      // fornecedor já escrita (row - 1).
      currentSubgrupoInfo.endRow = row - 1;
      subgruposInfo.push(currentSubgrupoInfo);
    }

    /**
     * Soma das células de total de subgrupo que atendem ao filtro.
     *
     * Devolve null quando não há subgrupo elegível ou quando o resultado
     * divergiria do total que o sistema calculou — nesse caso a linha continua
     * como valor. A conferência existe porque as duas somas percorrem caminhos
     * diferentes: `calcularTotais` agrupa por número de linha ("2."), o Excel
     * agrupa pelo número do grupo lido de "ITEM 2". Elas coincidem na
     * planilha normal, e é melhor a exportação bater com a tela do que exibir
     * uma fórmula elegante com outro número.
     */
    const somaDeSubgrupos = (
      filtro: (sg: (typeof subgruposInfo)[number]) => boolean,
      totalEsperado: number,
    ): string | null => {
      const celulas = subgruposInfo
        .filter((sg) => sg.endRow && sg.endRow >= sg.startRow)
        .filter(filtro)
        .map((sg) => `I${sg.startRow}`);

      if (celulas.length === 0) return null;

      const somaDosTotais = subgruposInfo
        .filter((sg) => sg.endRow && sg.endRow >= sg.startRow)
        .filter(filtro)
        .reduce((acc, sg) => acc + sg.total, 0);

      if (Math.abs(somaDosTotais - totalEsperado) >= 0.01) {
        console.warn(
          "Excel: soma dos subgrupos difere do total calculado; linha exportada como valor.",
          { somaDosTotais, totalEsperado },
        );
        return null;
      }

      return celulas.join("+");
    };

    // Adicionar SUB TOTAL no final de todos os dados
    const totalGeral = totais.subtotal;

    // Linha SUB TOTAL
    ws[`B${row}`] = cell("SUB TOTAL", {
      font: {
        name: "Trebuchet MS",
        sz: 14,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { horizontal: "center", vertical: "center" },
    });

    // Criar células vazias e mesclar B:H
    for (let c = 2; c <= 7; c++) {
      ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("", {
        fill: { fgColor: { rgb: "000000" } },
      });
    }
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 7 } });

    // Coluna I - Total geral
    const estiloBarraPreta = {
      font: {
        name: "Trebuchet MS",
        sz: 14,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { horizontal: "right" as const, vertical: "center" as const },
    };
    const formulaSubTotal = somaDeSubgrupos(() => true, totalGeral);
    ws[`I${row}`] = formulaSubTotal
      ? cellFormula(formulaSubTotal, totalGeral, estiloBarraPreta)
      : cell(totalGeral, estiloBarraPreta, true);

    row++;

    // Processar totais dos subgrupos - mesclar colunas H e I verticalmente
    subgruposInfo.forEach((subgrupo) => {
      if (
        subgrupo.startRow &&
        subgrupo.endRow &&
        subgrupo.endRow >= subgrupo.startRow
      ) {
        // Total do subgrupo: soma da faixa de H do próprio subgrupo. startRow
        // é a linha seguinte ao cabeçalho preto, então a faixa cobre só os
        // itens — e as linhas de fornecedor não escrevem em H (ficam sob a
        // mesclagem), o que evita contar o mesmo item duas vezes.
        ws[`I${subgrupo.startRow}`] = cellFormula(
          `SUM(H${subgrupo.startRow}:H${subgrupo.endRow})`,
          subgrupo.total,
          {
            font: { name: "Trebuchet MS", sz: 14, bold: true },
            alignment: { horizontal: "right", vertical: "center" },
          },
        );

        // Criar células vazias para as demais linhas do subgrupo antes de mesclar
        for (let r = subgrupo.startRow + 1; r <= subgrupo.endRow; r++) {
          ws[`I${r}`] = cell("", {
            font: { name: "Trebuchet MS", sz: 14, bold: true },
            alignment: { horizontal: "right", vertical: "center" },
          });
        }

        // Mesclar I do inicio ao fim do subgrupo (todo o subgrupo preto)
        if (subgrupo.endRow > subgrupo.startRow) {
          ws["!merges"].push({
            s: { r: subgrupo.startRow - 1, c: 8 },
            e: { r: subgrupo.endRow - 1, c: 8 },
          });
        }
      }
    });

    /*
    // =============================
    // RESUMO POR CATEGORIA (parte de baixo)
    // =============================
    // Agregar totais por grupo (1 = Essenciais, 2 = Complementares, 3+ = Serviços ou conforme nome do grupo)
    const totaisPorGrupoNumero: Record<number, number> = {};
    subgruposInfo.forEach(sg => {
      if (!totaisPorGrupoNumero[sg.grupoNumero]) totaisPorGrupoNumero[sg.grupoNumero] = 0;
      totaisPorGrupoNumero[sg.grupoNumero] += sg.total;
    });

    // Helper para escrever uma linha de resumo E/H
    const writeResumoLinha = (rotulo: string, valor: number) => {
      ws[`E${row}`] = cell(rotulo, {
        font: { name: 'Trebuchet MS', sz: 14, bold: true },
        alignment: { horizontal: 'left', vertical: 'center' }
      });
      ws[`H${row}`] = cell(valor || 0, {
        font: { name: 'Trebuchet MS', sz: 14, bold: true },
        alignment: { horizontal: 'right', vertical: 'center' }
      }, true);
      row++;
    };

    // Mapear labels padrão
    const totalEssenciais = totaisPorGrupoNumero[1] || 0;
    const totalComplementares = totaisPorGrupoNumero[2] || 0;
    // Para grupos >= 3, somar em "Serviços" por padrão (ou Despesas Acessórias, dependendo do título real)
    let totalServicos = 0;
    Object.keys(totaisPorGrupoNumero).forEach(k => {
      const g = parseInt(k, 10);
      if (g >= 3) totalServicos += totaisPorGrupoNumero[g] || 0;
    });

    // Escrever linhas de resumo (E/H)
    writeResumoLinha('Essenciais', totalEssenciais);
    writeResumoLinha('Complementares', totalComplementares);
    writeResumoLinha('Serviços', totalServicos);

    // Linha em branco
    row++;

    */

    // =============================
    // RESUMO POR CATEGORIA (usando totais calculados)
    // =============================
    const totalEssenciais = totais.totalServicosEssenciais;
    const totalComplementares = totais.totalServicosComplementares;
    // const totalServicos = totais.totalDespesasAcessorias;

    // =============================
    // BLOCO DE TOTAIS 152–159 (modelo)
    // Linhas 152–156: rótulos em C–D (mescladas), valores em I; tudo em negrito
    // Impostos, Honorários, Taxa: percentuais aplicados sobre Complementares por padrão
    // =============================
    // Só rótulo: os valores vêm calculados em `totais`, para a planilha na tela
    // e o Excel nunca mostrarem números diferentes.
    const percentImpostos = planilha?.percentImpostos ?? PERCENT_IMPOSTOS;
    // Do cliente da planilha; a API manda em pontos percentuais.
    const percentHonorarios = taxaDeHonorarios(planilha);
    const percentTaxaAdministracao =
      planilha?.percentTaxaAdministracao ?? PERCENT_TAXA_ADMINISTRACAO;

    const baseEssenciais = totalEssenciais;
    const baseComplementares = totalComplementares;
    const impostosValor = totais.impostos;
    const honorariosValor = totais.totalHonorarios;
    const taxaAdmValor = totais.taxaAdministracao;

    const estiloValorTotais = {
      font: { name: "Trebuchet MS", sz: 14, bold: true },
      alignment: { horizontal: "right" as const, vertical: "center" as const },
    };

    /**
     * Escreve uma linha do bloco de totais e devolve o número da linha, para
     * que as linhas seguintes possam referenciá-la por célula. Passe `formula`
     * quando o valor for derivado: aí a célula sai como fórmula e o número
     * calculado aqui vira só o resultado em cache.
     */
    const writeLineCD_I = (
      label: string,
      value: number | string,
      isCurrency = true,
      formula?: string,
    ) => {
      const linha = row;
      ws[`C${row}`] = cell(label, {
        font: { name: "Trebuchet MS", sz: 14, bold: true },
        alignment: { horizontal: "left", vertical: "center" },
      });
      ws[`D${row}`] = cell("", {});
      ws["!merges"].push({ s: { r: row - 1, c: 2 }, e: { r: row - 1, c: 3 } });
      ws[`I${row}`] =
        formula && typeof value === "number"
          ? cellFormula(formula, value, estiloValorTotais, isCurrency)
          : cell(
              value,
              estiloValorTotais,
              isCurrency && typeof value === "number",
            );
      row++;
      return linha;
    };

    // Base de Taxas e Seguros entra como número na fórmula, não como
    // referência: ela não tem linha própria no bloco de totais, e apontar para
    // a célula do subgrupo no corpo da planilha amarraria a fórmula ao
    // desenho das mesclagens. Vem do mesmo cálculo da tela.
    const baseTaxas = totais.totalTaxasESeguros || 0;

    /** Valor monetário na fórmula: duas casas, como qualquer dinheiro. */
    const num = (n: number) => n.toFixed(2);

    /**
     * Alíquota na fórmula. NÃO usa toFixed(2): 0,1425 viraria 0,14 e o imposto
     * sairia errado no Excel — a fórmula precisa da alíquota cheia.
     */
    const taxa = (n: number) => String(n);

    // As bases somam as células de total de subgrupo, que por sua vez somam a
    // faixa de subtotais dos itens. Com isso a cadeia é contínua: editar
    // quantidade, diária ou unitário de um item recalcula o subtotal, o total
    // do subgrupo, esta base e tudo o que deriva dela até o Total Geral.
    //
    // ITEM 1 é o grupo dos essenciais e ITEM 2 o dos complementares — mesma
    // convenção usada no resto do arquivo. somaDeSubgrupos devolve null e a
    // linha volta a ser valor se o resultado divergiria do total da tela.
    const linhaEssenciais = writeLineCD_I(
      "Total Custos Serviços Essenciais",
      baseEssenciais,
      true,
      somaDeSubgrupos((sg) => sg.grupoNumero === 1, baseEssenciais) ?? undefined,
    );
    const linhaComplementares = writeLineCD_I(
      "Total Custos Serviços Complementares",
      baseComplementares,
      true,
      somaDeSubgrupos((sg) => sg.grupoNumero === 2, baseComplementares) ??
        undefined,
    );

    const celComplementares = `I${linhaComplementares}`;

    // Imposto por dentro: a alíquota incide sobre o valor já com imposto, então
    // é base/(1-alíquota) - base, e não base*alíquota.
    const linhaImpostos = writeLineCD_I(
      `Impostos (${(percentImpostos * 100).toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
      })}%)`,
      impostosValor,
      true,
      `${celComplementares}/(1-${taxa(percentImpostos)})-${celComplementares}`,
    );

    // Honorários não incidem sobre Taxas e Seguros, que tem a taxa de
    // administração própria — daí a subtração da base.
    const linhaHonorarios = writeLineCD_I(
      `Total Honorários ${(percentHonorarios * 100).toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
      })}%`,
      honorariosValor,
      true,
      baseTaxas > 0
        ? `(${celComplementares}-${num(baseTaxas)})*${taxa(percentHonorarios)}`
        : `${celComplementares}*${taxa(percentHonorarios)}`,
    );

    const linhaTaxaAdm = writeLineCD_I(
      `Taxa de Administração ${(percentTaxaAdministracao * 100).toLocaleString(
        "pt-BR",
        { maximumFractionDigits: 2 },
      )}%`,
      taxaAdmValor,
      true,
      // Sem subgrupo de Taxas e Seguros não há o que multiplicar: uma fórmula
      // "=0*6%" só confundiria quem abre a planilha para auditar.
      baseTaxas > 0
        ? `${num(baseTaxas)}*${taxa(percentTaxaAdministracao)}`
        : undefined,
    );

    // Linha 157: Total (VALOR UTILIZADO NA AS) — barra preta B–H, valor em I
    const totalValorUtilizadoNAS = totais.totalValorAS;
    ws[`B${row}`] = cell("TOTAL (VALOR UTILIZADO NA AS)", {
      font: {
        name: "Trebuchet MS",
        sz: 14,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { horizontal: "left", vertical: "center" },
    });
    for (let c = 2; c <= 7; c++) {
      ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("", {
        fill: { fgColor: { rgb: "000000" } },
      });
    }
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 7 } });
    const linhaValorAS = row;
    // Soma pura de células: mexeu numa linha acima, este total acompanha.
    ws[`I${row}`] = cellFormula(
      `I${linhaEssenciais}+I${linhaComplementares}+I${linhaImpostos}+I${linhaHonorarios}+I${linhaTaxaAdm}`,
      totalValorUtilizadoNAS,
      {
        font: {
          name: "Trebuchet MS",
          sz: 14,
          bold: true,
          color: { rgb: "FFFFFF" },
        },
        fill: { fgColor: { rgb: "000000" } },
        alignment: { horizontal: "right", vertical: "center" },
      },
    );
    row++;

    // Linha 158: Total Custos Despesas Acessórias — barra cinza B–H, valor em I
    const totalDespesasAcessorias = totais.totalDespesasAcessorias;
    ws[`B${row}`] = cell("Total Custos Despesas Acessórias", {
      font: {
        name: "Trebuchet MS",
        sz: 14,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "808080" } },
      alignment: { horizontal: "left", vertical: "center" },
    });
    for (let c = 2; c <= 7; c++) {
      ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("", {
        fill: { fgColor: { rgb: "808080" } },
      });
    }
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 7 } });
    const linhaAcessorias = row;
    ws[`I${row}`] = cell(
      totalDespesasAcessorias,
      {
        font: {
          name: "Trebuchet MS",
          sz: 14,
          bold: true,
          color: { rgb: "FFFFFF" },
        },
        fill: { fgColor: { rgb: "808080" } },
        alignment: { horizontal: "right", vertical: "center" },
      },
      true,
    );
    row++;

    // Linha 159: Total Geral de Custo do Evento — barra preta B–H, valor em I
    const totalGeralEvento = totais.totalGeralEvento;
    ws[`B${row}`] = cell("Total Geral de Custo do Evento", {
      font: {
        name: "Trebuchet MS",
        sz: 14,
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: { fgColor: { rgb: "000000" } },
      alignment: { horizontal: "left", vertical: "center" },
    });
    for (let c = 2; c <= 7; c++) {
      ws[XLSX.utils.encode_cell({ r: row - 1, c })] = cell("", {
        fill: { fgColor: { rgb: "000000" } },
      });
    }
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 7 } });
    ws[`I${row}`] = cellFormula(
      `I${linhaValorAS}+I${linhaAcessorias}`,
      totalGeralEvento,
      {
        font: {
          name: "Trebuchet MS",
          sz: 14,
          bold: true,
          color: { rgb: "FFFFFF" },
        },
        fill: { fgColor: { rgb: "000000" } },
        alignment: { horizontal: "right", vertical: "center" },
      },
    );
    row++;

    // Linha em branco após Total Geral
    row++;

    // Mini tabela de resumo de Serviços Complementares (Equipamentos / Materiais / Serviços)
    const totalServicosComplementares = totais.totalServicosComplementares; // grupo 2
    // Header: E-F-G mesclado, H-I mesclado
    ws[`E${row}`] = cell("SERVIÇOS COMPLEMENTARES", {
      font: {
        name: "Trebuchet MS",
        sz: 12,
        bold: true,
        color: { rgb: "000000" },
      },
      fill: { fgColor: { rgb: "C0C0C0" } },
      alignment: { horizontal: "center", vertical: "center" },
    });
    ws[`F${row}`] = cell("", { fill: { fgColor: { rgb: "C0C0C0" } } });
    ws[`G${row}`] = cell("", { fill: { fgColor: { rgb: "C0C0C0" } } });
    ws["!merges"].push({ s: { r: row - 1, c: 4 }, e: { r: row - 1, c: 6 } });

    ws[`H${row}`] = cell(
      totalServicosComplementares,
      {
        font: {
          name: "Trebuchet MS",
          sz: 12,
          bold: true,
          color: { rgb: "000000" },
        },
        fill: { fgColor: { rgb: "C0C0C0" } },
        alignment: { horizontal: "right", vertical: "center" },
      },
      true,
    );
    ws[`I${row}`] = cell("", { fill: { fgColor: { rgb: "C0C0C0" } } });
    ws["!merges"].push({ s: { r: row - 1, c: 7 }, e: { r: row - 1, c: 8 } });
    const miniHeaderRow = row;
    row++;

    const miniRows = [
      { label: "Equipamentos", value: totais.breakdownEquipamentos },
      { label: "Materiais", value: totais.breakdownMateriais },
      { label: "Serviços", value: totais.breakdownServicos },
    ];

    miniRows.forEach(({ label, value }) => {
      ws[`E${row}`] = cell(label, {
        font: { name: "Trebuchet MS", sz: 12, bold: false },
        alignment: { horizontal: "left", vertical: "center" },
      });
      ws[`F${row}`] = cell("");
      ws[`G${row}`] = cell("");
      ws["!merges"].push({ s: { r: row - 1, c: 4 }, e: { r: row - 1, c: 6 } });
      // Se o usuário preencheu um valor, exporta; senão zero com formato moeda
      ws[`H${row}`] = cell(
        value != null && value > 0 ? value : 0,
        {
          font: { name: "Trebuchet MS", sz: 12 },
          alignment: { horizontal: "right", vertical: "center" },
        },
        true,
      );
      ws[`I${row}`] = cell("");
      ws["!merges"].push({ s: { r: row - 1, c: 7 }, e: { r: row - 1, c: 8 } });
      row++;
    });

    // Aplicar bordas finas na mini tabela (E..I, header + 3 linhas)
    const border = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };
    for (
      let rIdx = miniHeaderRow - 1;
      rIdx < miniHeaderRow - 1 + 1 + miniRows.length;
      rIdx++
    ) {
      for (let cIdx = 4; cIdx <= 8; cIdx++) {
        const addr = XLSX.utils.encode_cell({ r: rIdx, c: cIdx });
        if (!ws[addr]) ws[addr] = cell("", {});
        ws[addr].s = ws[addr].s ? { ...ws[addr].s, border } : { border };
      }
    }

    // Quadro de resumo após Total Geral de Custo do Evento (E/H)
    // writeResumoLinha('Essenciais', totalEssenciais);
    // writeResumoLinha('Complementares', totalComplementares);
    // writeResumoLinha('Servicos', totalServicos);

    // =============================
    // BLOCO ORIENTAÇÕES (fixo) — Correção: título mesclado B–I, centralizado, bold 22
    // =============================
    const orientacoesTitulo = "ORIENTAÇÕES:";
    const orientacoesTexto =
      '1) Quando a diária não for aplicável para o item, considerar "1" na coluna correspondente;\n\n2) A linha amarela apresenta o menor valor cotado dentre os fornecedores apresentados para cada item correspondente.';
    const orientacoesCompleto = `${orientacoesTitulo}\n\n${orientacoesTexto}`;

    // Linha do título
    ws[`B${row}`] = cell(orientacoesCompleto, {
      font: { name: "Trebuchet MS", sz: 22, bold: true, wrapText: true },
      alignment: { horizontal: "center", vertical: "center" },
    });
    // Mesclar B até I
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 8 } });
    if (!ws["!rows"]) ws["!rows"] = [];
    ws["!rows"][row - 1] = { hpt: 150 };
    row++;

    // Linha do texto detalhado (centralizado, negrito)
    ws[`B${row}`] = cell("", {
      font: { name: "Trebuchet MS", sz: 22, bold: true },
      alignment: { horizontal: "center", vertical: "top", wrapText: true },
    });
    // Mesclar B até I para o texto
    ws["!merges"].push({ s: { r: row - 1, c: 1 }, e: { r: row - 1, c: 8 } });
    ws["!rows"][row - 1] = { hpt: 1 };
    row++;

    // Definir range
    ws["!ref"] = `A1:J${row}`;

    // Definir alturas das linhas de subgrupo
    if (!ws["!rows"]) ws["!rows"] = [];
    subgrupoRows.forEach((subgrupoRow) => {
      ws["!rows"][subgrupoRow - 1] = { hpt: 37.5, hpx: 37.5 };
    });

    // Larguras das colunas (0 - Tamanho das colunas)
    ws["!cols"] = [
      { wch: 5 }, // A
      { wch: 10 }, // B
      { wch: 75 }, // C
      { wch: 75 }, // D
      { wch: 10 }, // E - CORRIGIDO: era 10
      { wch: 25 }, // F
      { wch: 20 }, // G
      { wch: 22 }, // H
      { wch: 20 }, // I
      { wch: 50 }, // J
    ];

    // Adicionar ao workbook
    XLSX.utils.book_append_sheet(wb, ws, "Planejamento");

    // Gerar buffer e retornar
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "buffer" });
    return buffer;
  },
};
